import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Percent, Zap, Star, Truck, CreditCard, Gift, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RotatingSpotlight from '../components/RotatingSpotlight';
import RecommendedForYou from '../components/RecommendedForYou';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Percent,
    title: '0% Seller Fees',
    description: 'Keep 100% of your sale price. We charge buyers a small 3.5% transaction fee instead.',
    color: 'text-emerald-400'
  },
  {
    icon: Zap,
    title: 'AI Card Scanner',
    description: 'Upload a photo and our AI instantly identifies player, set, year, and grade.',
    color: 'text-amber-400'
  },
  {
    icon: Shield,
    title: 'Trust & Ratings',
    description: '5-star rating system with verified reviews. Trade with confidence.',
    color: 'text-blue-400'
  }
];

const steps = [
  {
    num: '01',
    title: 'Subscribe',
    description: '$20/month unlocks unlimited listings with 0% seller fees.'
  },
  {
    num: '02',
    title: 'Scan & List',
    description: 'Upload a photo. AI fills in the details. Set your price.'
  },
  {
    num: '03',
    title: 'Ship & Earn',
    description: 'Buyer pays. You ship. Get paid instantly to your account.'
  }
];

export const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState({ total_listings: 0, total_users: 0, total_sales: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Promo Banner - Added pt-20 to account for fixed navbar */}
      <div className="bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-amber-500/20 border-b border-amber-500/30 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center gap-3 text-center flex-wrap">
            <Gift className="w-5 h-5 text-amber-400 shrink-0" />
            <p className="text-sm sm:text-base text-white">
              <span className="font-semibold text-amber-400">Join Slab Society Gold</span>
              {' '}– Get your first month FREE with code{' '}
              <span className="font-mono font-bold bg-amber-500/30 px-2 py-0.5 rounded text-amber-300">SLAB100</span>
            </p>
            <Link to="/register">
              <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1 h-auto">
                Claim Offer
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center spotlight">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${process.env.REACT_APP_HERO_BG_URL}')` }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-24">
          <div className="max-w-3xl">
            <span className="badge badge-amber mb-6 animate-fade-in">
              Zero Seller Commission
            </span>
            
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase text-white leading-[0.9] animate-slide-up">
              The Premium<br />
              <span className="text-blue-400">Slab</span> Marketplace
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-zinc-400 max-w-xl animate-slide-up stagger-1">
              Buy and sell graded sports cards with zero seller fees. 
              Keep 100% of your sale price. Powered by AI card recognition.
            </p>
            
            <div className="flex flex-wrap gap-4 mt-10 animate-slide-up stagger-2">
              <Link to={isAuthenticated ? "/marketplace" : "/register"}>
                <Button className="btn-primary flex items-center gap-2" data-testid="hero-cta">
                  {isAuthenticated ? 'Browse Cards' : 'Start Selling'}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/marketplace">
                <Button className="btn-secondary" data-testid="hero-browse">
                  View Marketplace
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 pt-8 border-t border-white/10 animate-slide-up stagger-3">
              <div>
                <p className="font-mono text-3xl sm:text-4xl font-bold text-white">{stats.total_listings}</p>
                <p className="text-sm text-zinc-500 mt-1">Active Listings</p>
              </div>
              <div>
                <p className="font-mono text-3xl sm:text-4xl font-bold text-white">{stats.total_users}</p>
                <p className="text-sm text-zinc-500 mt-1">Collectors</p>
              </div>
              <div>
                <p className="font-mono text-3xl sm:text-4xl font-bold text-white">{stats.total_sales}</p>
                <p className="text-sm text-zinc-500 mt-1">Cards Sold</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Spotlight Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0A0A0C]">
        <div className="max-w-7xl mx-auto">
          <RotatingSpotlight />
        </div>
      </section>

      {/* Recommended For You Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <RecommendedForYou />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-blue mb-4">Why Slab Society</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white">
              Built for Collectors
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="stat-card hover:border-white/10 transition-colors group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div>
                  <feature.icon className={`w-10 h-10 ${feature.color} mb-4`} />
                  <h3 className="font-heading font-bold text-xl text-white mb-2 group-hover:text-blue-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-emerald mb-4">Simple Process</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={step.num} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-blue-500/50 to-transparent -translate-x-1/2" />
                )}
                <div className="stat-card h-full">
                  <div>
                    <span className="font-mono text-5xl font-bold text-blue-500/30">{step.num}</span>
                    <h3 className="font-heading font-bold text-2xl text-white mt-4 mb-2">{step.title}</h3>
                    <p className="text-zinc-400">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="badge badge-amber mb-4">Seller Membership</span>
          <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white mb-8">
            $20/Month. 0% Fees.
          </h2>
          
          <div className="stat-card p-8 text-left">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="font-mono text-5xl font-bold text-white">$20<span className="text-xl text-zinc-500">/mo</span></p>
                <p className="text-zinc-400 mt-2">Unlimited listings, zero commission</p>
              </div>
              <Link to={isAuthenticated ? "/subscription" : "/register"}>
                <Button className="btn-primary" data-testid="pricing-cta">
                  {isAuthenticated ? 'Subscribe Now' : 'Get Started'}
                </Button>
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-4 mt-8 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <span className="text-zinc-300">Secure payments</span>
              </div>
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-amber-400" />
                <span className="text-zinc-300">Verified seller badge</span>
              </div>
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-emerald-400" />
                <span className="text-zinc-300">Shipping integration</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-t from-blue-900/20 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white mb-6">
            Join The Society
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            From Doc Holliday: "Welcome to the future of sports card trading."
          </p>
          <Link to="/register">
            <Button className="btn-primary text-lg px-12" data-testid="final-cta">
              Create Free Account
            </Button>
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-4xl mx-auto">
          <div className="stat-card p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <img 
                src={process.env.REACT_APP_LOGO_URL} 
                alt="Slab Society Logo"
                className="w-32 h-32 rounded-2xl object-contain"
              />
              <div>
                <h2 className="font-heading font-bold text-3xl uppercase text-white mb-4">About Slab Society</h2>
                <p className="text-zinc-400 mb-4">
                  Founded by <span className="text-amber-500 font-semibold">Ron Manthei</span> (aka Doc Holliday), 
                  Slab Society is revolutionizing the sports card marketplace with a simple philosophy: 
                  sellers deserve to keep 100% of their earnings.
                </p>
                <p className="text-zinc-400">
                  Our mission is to create the most trusted, collector-focused platform where hobbyists 
                  and investors can buy and sell graded sports cards with zero seller fees, powered by 
                  cutting-edge AI technology.
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-zinc-500 text-sm">
                    <span className="text-white font-medium">Ron Manthei</span> — Founder & CEO
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
